import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionStatus } from '@core/requisitions-types';

import type { RequisitionsAbilities } from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionsDataProviderService } from '../../data/single-requisition/requisitions-data-provider.service';
import { RequisitionsDialogsService } from '../../services/requisitions-dialogs.service';
import { RequisitionsSharedActionsService } from '../../services/requisitions-shared-actions.service';

@Injectable()
export class CreateJobFacade {
  private readonly requisitionsDialogsService = inject(RequisitionsDialogsService);
  private readonly requisitionsSharedActionsService = inject(RequisitionsSharedActionsService);
  private readonly store = inject(Store);

  protected readonly requisitionsData = inject(RequisitionsDataProviderService);

  private requisition: Requisition;
  private abilities: RequisitionsAbilities;
  private currentUserId: number;
  private hasAccessToAllJobs: boolean;
  private canManageJobs: boolean;
  private isRequisitionReadOnly: boolean;
  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const requisitionsData = this.requisitionsData;

    requisitionsData.data$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(({ requisition, abilities, isRequisitionReadOnly }) => {
        this.requisition = requisition;
        this.abilities = abilities;
        this.isRequisitionReadOnly = isRequisitionReadOnly;
      });

    this.store
      .select(AbilitiesSelectors.abilityMap)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(enabledMap => {
        this.hasAccessToAllJobs = enabledMap[Ability.ViewAllJobs];
        this.canManageJobs = enabledMap[Ability.ManageJobs];
      });

    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(admin => (this.currentUserId = admin.id));
  }

  public get canCreateJob(): boolean {
    return this.abilities.manageJobs && this.isAssigningJobsAllowed && !this.isRequisitionReadOnly;
  }

  public get isAssigningJobsAllowed(): boolean {
    return this.requisition.status === RequisitionStatus.Approved;
  }

  public handleShouldShowApprovedDialog(): void {
    const approvalRequestedData = this.requisition.approvalRequest;
    if (
      this.requisition.status === RequisitionStatus.Approved &&
      approvalRequestedData?.requesterId === this.currentUserId &&
      !approvalRequestedData?.viewedByRequester
    ) {
      this.requisitionsDialogsService
        .openRequisitionApprovedSuccessDialog(
          {
            canCreateJob: this.canManageJobs,
            requisitionName: this.requisition.title,
            offerSlug: this.requisition?.offers[0]?.slug || null,
            hasAccessToJob: this.hasAccessToAllJobs,
          },
          {
            requisitionId: this.requisition.id,
          },
        )
        .pipe(filterBoolean)
        .subscribe(({ createJob }) => {
          if (createJob) {
            this.requisitionsSharedActionsService.createJob(this.requisition.id, true);
          }
        });
    }
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }
}
